import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { propertyManagerNotificationRepository } from '~/repositories/settings/propertyManagerNotificationRepository';
import type { Accessor } from 'solid-js';

interface PropertyManagerNotificationContextValue {
  updatePropertyManagerNotificationSettingsByLevel: (
    level: string,
    levelId: string,
    payload: MagicDoor.Api.PropertyManagerNotificationSettingsDto
  ) => Promise<void>;
  deletePropertyManagerNotificationSettingsByLevel: (level: string, levelId: string) => Promise<void>;
  propertyManagerNotificationSettings: Accessor<MagicDoor.Api.PropertyManagerNotificationSettingsDtoSettingsWithOverridesDto>;
  isLoading: Accessor<boolean>;
  getPropertyManagerNotificationSettings: () => void;
  updatePropertyManagerNotificationSettings: (data: MagicDoor.Api.PropertyManagerNotificationSettingsDto) => void;
  error: Accessor<Error | undefined>;
}

export const [PropertyManagerNotificationProvider, usePropertyManagerNotification] =
  createMagicDoorContext<PropertyManagerNotificationContextValue>('PropertyManagerNotification', () => {
    const repo = new propertyManagerNotificationRepository();

    const [isLoading, setIsLoading] = createSignal<boolean>(false);
    const [propertyManagerNotificationSettings, setPropertyManagerNotificationSettings] =
      createSignal<MagicDoor.Api.PropertyManagerNotificationSettingsDtoSettingsWithOverridesDto>(
        {} as MagicDoor.Api.PropertyManagerNotificationSettingsDtoSettingsWithOverridesDto
      );

    const [error, setError] = createSignal<Error | undefined>(undefined);

    const getPropertyManagerNotificationSettings = async () => {
      setIsLoading(true);
      try {
        const result = await repo.getPropertyManagerNotificationAll();
        setPropertyManagerNotificationSettings(result);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const updatePropertyManagerNotificationSettings = async (data: MagicDoor.Api.PropertyManagerNotificationSettingsDto) => {
      setIsLoading(true);
      try {
        await repo.updatePropertyManagerNotification(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const updatePropertyManagerNotificationSettingsByLevel = async (
      level: string,
      levelId: string,
      payload: MagicDoor.Api.PropertyManagerNotificationSettingsDto
    ) => {
      setIsLoading(true);
      try {
        await repo.updatePropertyManagerNotificationByLevel(level, levelId, payload);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const deletePropertyManagerNotificationSettingsByLevel = async (level: string, levelId: string) => {
      setIsLoading(true);
      try {
        await repo.deletePropertyManagerNotificationByLevel(level, levelId);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    return {
      error,
      propertyManagerNotificationSettings,
      isLoading,
      getPropertyManagerNotificationSettings,
      updatePropertyManagerNotificationSettings,
      updatePropertyManagerNotificationSettingsByLevel,
      deletePropertyManagerNotificationSettingsByLevel,
    };
  });
